import { createFhirDateTime } from '@quromedical/fhir-common';
import type {
  MeasurementName,
  ObservationVitalMeasurement,
} from '@quromedical/models';
import { compact } from '@quromedical/utils';
import { CrudForm, type SubmissionHandler } from 'components/form';
import type { Field } from 'components/types';
import { titles, units } from 'core/vitals';
import { logger } from 'helpers/logger';
import { useCallback, useMemo, useState } from 'react';
import { strings } from 'strings';
import type { ObjectType } from 'validation';
import * as yup from 'yup';

import type { VitalObservationForm } from './types';

export interface VitalObservationCreateFormData {
  /**
   * Currently selected/active measurement
   */
  selectedMeasure: MeasurementName;
  value: number;
  /**
   * Not editable on the form, preset on the form, display only
   */
  unit?: string;
  /**
   * The date/time in the format as provided by masked datetime input
   */
  time: string;
}

/**
 * Generate schema such that the provided measurements are required
 */
const createSchema = (measureOptions: MeasurementName[]) =>
  yup.object<ObjectType<VitalObservationCreateFormData>>({
    selectedMeasure: yup.string().required().equals(measureOptions),
    value: yup.number().required(),
    time: yup.date().required(),
  });

const createFields = (
  measureOptions: MeasurementName[],
  selectedMeasure?: MeasurementName,
): Field<VitalObservationCreateFormData>[] => {
  const selectedMeasureField:
    | Field<VitalObservationCreateFormData>
    | undefined =
    measureOptions.length > 1
      ? {
          subfields: [
            {
              key: 'selectedMeasure',
              type: 'combobox-single',
              label: 'Select Measurement',
              fetcher: measureOptions.map((measure) => ({
                value: measure,
                display: titles[measure] || measure,
              })),
            },
          ],
        }
      : undefined;

  return compact([
    selectedMeasureField,
    {
      subfields: [
        {
          key: 'value',
          type: 'number-box',
          keyboardType: 'decimal-pad',
          label: strings.VitalObservationFormLabelValue,
        },
        {
          key: 'unit',
          type: 'text-display',
          label: strings.VitalObservationFormLabelUnit,
          text: selectedMeasure ? units[selectedMeasure] : '',
          backgroundColor: 'base-grey',
        },
      ],
    },
    {
      subfields: [
        {
          key: 'time',
          type: 'date-component',
          maskType: 'datetime',
          label: strings.VitalObservationFormLabelDateTime,
        },
      ],
    },
  ]);
};

const getMeasuresFromForm = (
  formData: VitalObservationCreateFormData,
): ObservationVitalMeasurement[] => [
  {
    measurementName: formData.selectedMeasure,
    ts: new Date(formData.time).getTime(),
    value: formData.value,
  },
];

export const VitalObservationSingleCreateForm: VitalObservationForm = ({
  measurementNames,
  onSubmit,
  onCancel,
}) => {
  const initialValues: Partial<VitalObservationCreateFormData> = {
    time: createFhirDateTime(new Date()),
    selectedMeasure: measurementNames[0],
  };

  const [formData, setFormData] =
    useState<Partial<VitalObservationCreateFormData>>(initialValues);

  const handleSubmit = useCallback<
    SubmissionHandler<VitalObservationCreateFormData>
  >(
    async (data: VitalObservationCreateFormData) => {
      try {
        const results = getMeasuresFromForm(data);
        await onSubmit(results);
        return {};
      } catch (error) {
        logger.error(error);
        return { error };
      }
    },
    [onSubmit],
  );

  const fields = useMemo(
    () => createFields(measurementNames, formData.selectedMeasure),
    [measurementNames, formData],
  );

  const schema = useMemo(
    () => createSchema(measurementNames),
    [measurementNames],
  );

  return (
    <CrudForm
      key={formData.selectedMeasure}
      isNotResponsive
      hasCard={false}
      fields={fields}
      onSubmit={handleSubmit}
      buttonText={strings.ButtonTextSubmit}
      onSecondarySubmit={onCancel}
      secondaryButtonText={strings.ButtonTextCancel}
      showSecondarySubmitButton
      validationSchema={schema}
      initialValues={formData}
      onChangeDebounceTime={0}
      onChange={setFormData}
    />
  );
};
